import config from '../config';
import Logger from '../logger';
import event from '../mixins/event';

class ThemeImageAndNav {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * Class constructor
	 */
	constructor(dom) {

		this.NAME = 'ThemeImageAndNav';

		this.dom = dom;
		this.imageDesktopContainerDom = this.dom.querySelector('[data-theme-image-and-nav-image-desktop]');
		this.imageMobileContainerDom = this.dom.querySelector('[data-theme-image-and-nav-image-mobile]');
		this.originalDesktopHtml = this.imageDesktopContainerDom.innerHTML;
		this.originalMobileHtml = this.imageMobileContainerDom.innerHTML;
		this.linkDoms = this.dom.querySelectorAll('[data-theme-image-and-nav-link]');
		this._imageSwapGeneration = 0;
	}

	/**
	 * init - Initialize the component
	 **/
	init() {
		Logger.info(`${this.NAME} > init`);
		this.initLinks();
	}

	/*
		initLinks - Initialize the links
	 */
	initLinks() {
		Logger.info(`${this.NAME} > initLinks`);
		this.linkDoms.forEach(linkDom => {
			Logger.info(`${this.NAME} > initLinks > linkDom`, linkDom);
			linkDom.addEventListener('mouseenter', this.handleLinkHover.bind(this));
			linkDom.addEventListener('mouseleave', this.handleLinkHoverLeave.bind(this));
		});
	}

	/**
	 * Wait for images in a detached tree to load and decode, then move nodes into the container.
	 * Avoids clearing the visible slot first (which flashes the container background).
	 * @param {HTMLElement} container
	 * @param {string} htmlString
	 * @param {number} generation — if stale, swap is skipped (rapid hover between links)
	 * @returns {Promise<void>}
	 */
	async swapImageContainerWhenReady(container, htmlString, generation) {
		const wrapper = document.createElement('div');
		wrapper.innerHTML = htmlString;
		const imgs = [...wrapper.querySelectorAll('img')];

		// Cloned markup keeps loading="lazy"; lazy images do not reliably load in a detached
		// subtree, so load never fires and the swap never runs.
		imgs.forEach((img) => {
			img.loading = 'eager';
		});

		await Promise.all(imgs.map((img) => this.whenImageReady(img)));

		if (generation !== this._imageSwapGeneration) {
			return;
		}

		const nodes = Array.from(wrapper.childNodes);
		container.replaceChildren(...nodes);
	}

	/**
	 * @param {HTMLImageElement} img
	 * @returns {Promise<void>}
	 */
	whenImageReady(img) {
		return new Promise((resolve) => {
			const finish = async () => {
				if (img.decode) {
					try {
						await img.decode();
					} catch {
						// decode() rejects on corrupt image; still swap
					}
				}
				resolve();
			};

			if (img.complete && img.naturalHeight !== 0) {
				void finish();
				return;
			}

			img.addEventListener('load', () => void finish(), { once: true });
			img.addEventListener('error', () => void finish(), { once: true });
		});
	}

	/**
	 * handleLinkHover - Handle the link hover
	 * Replace the two images with the hover image
	 * @param {Event} event - The event object
	 * @returns {void}
	 */
	handleLinkHover(event) {
		Logger.info(`${this.NAME} > handleLinkHover`);
		const linkDom = event.currentTarget;
		const linkParentDom = linkDom.parentElement;
		const generation = ++this._imageSwapGeneration;

		const imagePreviewDesktopDom = linkParentDom.querySelector('[data-theme-image-and-nav-link-image-desktop]');
		const imagePreviewMobileDom = linkParentDom.querySelector('[data-theme-image-and-nav-link-image-mobile]');
		const desktopHtml = imagePreviewDesktopDom ? imagePreviewDesktopDom.innerHTML : '';
		const mobileHtml = imagePreviewMobileDom ? imagePreviewMobileDom.innerHTML : '';

		void Promise.all([
			this.swapImageContainerWhenReady(this.imageDesktopContainerDom, desktopHtml, generation),
			this.swapImageContainerWhenReady(this.imageMobileContainerDom, mobileHtml, generation),
		]);
	}

	/**
	 * handleLinkHoverLeave - Handle the link hover leave
	 * Replace the two images with the original images
	 * @param {Event} event - The event object
	 * @returns {void}
	 */
	handleLinkHoverLeave(event) {
		Logger.info(`${this.NAME} > handleLinkHoverLeave`);
		const generation = ++this._imageSwapGeneration;

		void Promise.all([
			this.swapImageContainerWhenReady(this.imageDesktopContainerDom, this.originalDesktopHtml, generation),
			this.swapImageContainerWhenReady(this.imageMobileContainerDom, this.originalMobileHtml, generation),
		]);
	}
}

Object.assign(ThemeImageAndNav.prototype, event);

export default ThemeImageAndNav;
